import React, { useEffect, useMemo } from 'react';
import { createUseStyles } from 'react-jss';

import { InputLabel, Select, SelectProps, useOptionalField } from '@app/form';
import { Theme } from '@app/theme';

import {
  useConferencingAuthorizeUrl,
  useMyConferencingProviders,
  VideoCallProvider,
} from '@app/api';
import { Button, InformationBox } from '@app/core';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  subFieldContainer: {
    backgroundColor: theme.colors.grayBackground,
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    marginTop: theme.marginBase,
  },
  buttonIcon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
  },
  errorBox: {
    marginTop: theme.marginBase,
  },
}));

export const ZoomAccountSelect = (props: SelectProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: authConferencingUrl } = useConferencingAuthorizeUrl(
    VideoCallProvider.ZOOM,
  );
  const {
    field: { value },
    helpers,
  } = useOptionalField(props);
  const setValue = helpers?.setValue;

  const { data: profiles } = useMyConferencingProviders();
  const options = useMemo(() => {
    const res: any[] = [];
    if (!profiles) return res;
    for (const profile of profiles) {
      if (profile.providerName === VideoCallProvider.ZOOM) {
        res.push({
          label: profile.profileName,
          value: profile.profileId,
        });
      }
    }
    return res;
  }, [profiles]);

  const hasMismatchError = useMemo(() => {
    if (!profiles) {
      return false;
    }
    return !!value && !options.some((option) => option.value === value);
  }, [profiles, options, value]);

  useEffect(() => {
    if (options.length === 1 && props.name && !value && setValue) {
      setValue(props.name, options[0].value);
    }
  }, [setValue, options, props.name && value]);

  if (!options.length && authConferencingUrl)
    return (
      <div className={classes.subFieldContainer}>
        <InputLabel
          label={t(
            'editSession.settings.moderator.conferencingProvider.linkZoom',
          )}
        />
        <Button
          text={t(
            'editSession.settings.moderator.conferencingProvider.addZoom',
          )}
          variant="transparent"
          color="blue"
          target="_blank"
          small
          href={authConferencingUrl.redirectUrl}
        />
      </div>
    );

  return (
    <div className={classes.subFieldContainer}>
      <InputLabel
        label={t(
          'editSession.settings.moderator.conferencingProvider.chooseZoom',
        )}
      />
      <Select {...props} options={options} noMargin />
      {!!hasMismatchError && (
        <InformationBox variant="danger" className={classes.errorBox}>
          {t(
            'editSession.settings.moderator.conferencingProvider.mismatchError',
          )}
        </InformationBox>
      )}
    </div>
  );
};
